import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react';
import { Modal, Table, Tag, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button } from '../button';
import { ColumnProps } from 'antd/es/table';
import moment from 'moment-timezone';
import { useStoresStore } from '../../contexts/mobx';
import { StoreChangeHistory } from '../../types';
import { Text } from '../../utils/text';
import { Pagination } from '../pagination';
import { TEN_PER_PAGE } from '../../utils/const';
import style from './store-history-modal.module.scss';

export interface StoreHistoryModalProps {
    open: boolean;
    onClose: () => void;
}

const ACTION_COLORS: Record<string, string> = {
    added: 'green',
    modified: 'blue',
    deleted: 'red',
};

const getActionColor = (action: string): string => {
    return ACTION_COLORS[action] ?? 'default';
};

const FIELD_LABELS: Record<string, string> = {
    id: 'ID',
    nref: 'Reference',
    title: 'Title',
    status: 'Status',
    carWashStatus: 'Car Wash',
    carWashClosedOn: 'Car Wash Closing Date',
    brand: 'Brand',
    tel: 'Phone',
    img: 'Image',
    lat: 'Latitude',
    lng: 'Longitude',
    street: 'Street',
    city: 'City',
    post: 'Postal Code',
    services: 'Services',
    hours: 'Hours',
};

const DAYS_LABELS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const SECTION_TITLES = {
    generalInfo: 'General Information',
    location: 'Location',
    services: 'Services',
    hours: 'Hours',
};

type DiffStatus = 'added' | 'removed' | 'modified' | 'unchanged';

interface StoreValues {
    id?: number;
    nref?: string;
    title?: string | null;
    status?: string;
    carWashStatus?: string;
    carWashClosedOn?: string | null;
    brand?: string | null;
    tel?: string | null;
    img?: string | null;
    lat?: number | null;
    lng?: number | null;
    addr?: {
        street?: string | null;
        city?: string | null;
        post?: string | null;
    };
    services?: string[];
    hours?: (string | null)[];
}

function getFieldDiffStatus(
    beforeValue: unknown,
    afterValue: unknown
): DiffStatus {
    const beforeExists = beforeValue !== undefined && beforeValue !== null;
    const afterExists = afterValue !== undefined && afterValue !== null;

    if (!beforeExists && afterExists) return 'added';
    if (beforeExists && !afterExists) return 'removed';
    if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue))
        return 'modified';
    return 'unchanged';
}

function formatValue(value: unknown): string {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'string') return value || '-';
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) return value.join(', ') || '-';
    return String(value);
}

interface FieldRowProps {
    label: string;
    beforeValue: unknown;
    afterValue: unknown;
    side: 'before' | 'after';
}

function FieldRow({ label, beforeValue, afterValue, side }: FieldRowProps) {
    const diffStatus = getFieldDiffStatus(beforeValue, afterValue);
    const value = side === 'before' ? beforeValue : afterValue;

    let statusClass = '';
    if (side === 'before' && diffStatus === 'removed') {
        statusClass = style.diff_removed;
    } else if (side === 'after' && diffStatus === 'added') {
        statusClass = style.diff_added;
    } else if (diffStatus === 'modified') {
        statusClass = style.diff_modified;
    }

    return (
        <div className={`${style.field_row} ${statusClass}`}>
            <span className={style.field_label}>{label}</span>
            <span className={style.field_value}>{formatValue(value)}</span>
        </div>
    );
}

interface SectionCardProps {
    title: string;
    children: React.ReactNode;
}

function SectionCard({ title, children }: SectionCardProps) {
    return (
        <div className={style.section_card}>
            <div className={style.section_title}>{title}</div>
            <div className={style.section_content}>{children}</div>
        </div>
    );
}

interface HoursTableProps {
    beforeHours: (string | null)[] | undefined;
    afterHours: (string | null)[] | undefined;
    side: 'before' | 'after';
}

function HoursTable({ beforeHours, afterHours, side }: HoursTableProps) {
    const hours = side === 'before' ? beforeHours : afterHours;

    if (!hours || hours.length === 0) {
        return <span className={style.field_value}>-</span>;
    }

    return (
        <div className={style.hours_grid}>
            {hours.slice(0, 7).map((hour, index) => {
                const beforeHour = beforeHours?.[index];
                const afterHour = afterHours?.[index];
                const diffStatus = getFieldDiffStatus(beforeHour, afterHour);

                let statusClass = '';
                if (side === 'before' && diffStatus === 'removed') {
                    statusClass = style.diff_removed;
                } else if (side === 'after' && diffStatus === 'added') {
                    statusClass = style.diff_added;
                } else if (diffStatus === 'modified') {
                    statusClass = style.diff_modified;
                }

                return (
                    <div
                        key={index}
                        className={`${style.hour_row} ${statusClass}`}
                    >
                        <span className={style.day_label}>
                            {DAYS_LABELS[index]}
                        </span>
                        <span className={style.hour_value}>{hour || '-'}</span>
                    </div>
                );
            })}
        </div>
    );
}

interface ServicesListProps {
    beforeServices: string[] | undefined;
    afterServices: string[] | undefined;
    side: 'before' | 'after';
}

function ServicesList({
    beforeServices,
    afterServices,
    side,
}: ServicesListProps) {
    const services = side === 'before' ? beforeServices : afterServices;

    if (!services || services.length === 0) {
        return <span className={style.field_value}>-</span>;
    }

    const beforeSet = new Set(beforeServices ?? []);
    const afterSet = new Set(afterServices ?? []);

    return (
        <div className={style.services_list}>
            {services.map((service) => {
                const isRemoved = side === 'before' && !afterSet.has(service);
                const isAdded = side === 'after' && !beforeSet.has(service);

                return (
                    <span
                        key={service}
                        className={`${style.service_tag} ${
                            isRemoved ? style.service_removed : ''
                        } ${isAdded ? style.service_added : ''}`}
                    >
                        {service}
                    </span>
                );
            })}
        </div>
    );
}

interface StoreChangesDiffProps {
    beforeValues: StoreValues | null;
    afterValues: StoreValues | null;
    side: 'before' | 'after';
    isNewStore: boolean;
    isDeleted: boolean;
}

function StoreChangesDiff({
    beforeValues,
    afterValues,
    side,
    isNewStore,
    isDeleted,
}: StoreChangesDiffProps) {
    if (side === 'before' && isNewStore) {
        return (
            <div className={style.empty_state}>
                {Text.storeHistoryComponent.newStore}
            </div>
        );
    }

    if (side === 'after' && isDeleted) {
        return (
            <div className={style.empty_state}>
                {Text.storeHistoryComponent.deleted}
            </div>
        );
    }

    const before = beforeValues ?? {};
    const after = afterValues ?? {};

    return (
        <div className={style.sections_container}>
            <SectionCard title={SECTION_TITLES.generalInfo}>
                <FieldRow
                    label={FIELD_LABELS.nref}
                    beforeValue={before.nref}
                    afterValue={after.nref}
                    side={side}
                />
                <FieldRow
                    label={FIELD_LABELS.title}
                    beforeValue={before.title}
                    afterValue={after.title}
                    side={side}
                />
                <FieldRow
                    label={FIELD_LABELS.status}
                    beforeValue={before.status}
                    afterValue={after.status}
                    side={side}
                />
                <FieldRow
                    label={FIELD_LABELS.carWashStatus}
                    beforeValue={before.carWashStatus}
                    afterValue={after.carWashStatus}
                    side={side}
                />
                <FieldRow
                    label={FIELD_LABELS.carWashClosedOn}
                    beforeValue={before.carWashClosedOn}
                    afterValue={after.carWashClosedOn}
                    side={side}
                />
                <FieldRow
                    label={FIELD_LABELS.brand}
                    beforeValue={before.brand}
                    afterValue={after.brand}
                    side={side}
                />
                <FieldRow
                    label={FIELD_LABELS.tel}
                    beforeValue={before.tel}
                    afterValue={after.tel}
                    side={side}
                />
            </SectionCard>

            <SectionCard title={SECTION_TITLES.location}>
                <FieldRow
                    label={FIELD_LABELS.street}
                    beforeValue={before.addr?.street}
                    afterValue={after.addr?.street}
                    side={side}
                />
                <FieldRow
                    label={FIELD_LABELS.city}
                    beforeValue={before.addr?.city}
                    afterValue={after.addr?.city}
                    side={side}
                />
                <FieldRow
                    label={FIELD_LABELS.post}
                    beforeValue={before.addr?.post}
                    afterValue={after.addr?.post}
                    side={side}
                />
                <FieldRow
                    label={FIELD_LABELS.lat}
                    beforeValue={before.lat}
                    afterValue={after.lat}
                    side={side}
                />
                <FieldRow
                    label={FIELD_LABELS.lng}
                    beforeValue={before.lng}
                    afterValue={after.lng}
                    side={side}
                />
            </SectionCard>

            <SectionCard title={SECTION_TITLES.services}>
                <ServicesList
                    beforeServices={before.services}
                    afterServices={after.services}
                    side={side}
                />
            </SectionCard>

            <SectionCard title={SECTION_TITLES.hours}>
                <HoursTable
                    beforeHours={before.hours}
                    afterHours={after.hours}
                    side={side}
                />
            </SectionCard>
        </div>
    );
}

export const StoreHistoryModal = observer(
    ({ open, onClose }: StoreHistoryModalProps) => {
        const store = useStoresStore();
        const [page, setPage] = useState(0);
        const [perPage, setPerPage] = useState(TEN_PER_PAGE);
        const [
            selectedRecord,
            setSelectedRecord,
        ] = useState<StoreChangeHistory | null>(null);

        useEffect(() => {
            if (open) {
                store.fetchStoreHistory(page, perPage);
            }
        }, [open, page, perPage, store]);

        const handleClose = useCallback(() => {
            setSelectedRecord(null);
            setPage(0);
            setPerPage(TEN_PER_PAGE);
            onClose();
        }, [onClose]);

        const handleBackToList = useCallback(() => {
            setSelectedRecord(null);
        }, []);

        const handleSelectRecord = useCallback((record: StoreChangeHistory) => {
            setSelectedRecord(record);
        }, []);

        const getStoreAddress = (record: StoreChangeHistory): string => {
            const values = (record.afterValues ??
                record.beforeValues) as StoreValues | null;
            if (!values?.addr) return '-';
            const { street, city, post } = values.addr;
            const parts = [street, city, post].filter(Boolean);
            return parts.length > 0 ? parts.join(', ') : '-';
        };

        const columns: ColumnProps<StoreChangeHistory>[] = useMemo(
            () => [
                {
                    title: Text.storeHistoryComponent.columns.date,
                    dataIndex: 'createdAt',
                    key: 'createdAt',
                    render: (createdAt: string) =>
                        moment(createdAt)
                            .tz('America/New_York')
                            .format('MMMM Do, YYYY, h:mm A'),
                },
                {
                    title: Text.storeHistoryComponent.columns.storeNref,
                    dataIndex: 'storeNref',
                    key: 'storeNref',
                    width: 100,
                    render: (nref: string | null) => nref ?? 'N/A',
                },
                {
                    title: Text.storeHistoryComponent.columns.address,
                    key: 'address',
                    ellipsis: true,
                    render: (_: unknown, record: StoreChangeHistory) =>
                        getStoreAddress(record),
                },
                {
                    title: Text.storeHistoryComponent.columns.action,
                    dataIndex: 'action',
                    key: 'action',
                    width: 120,
                    render: (action: string) => (
                        <Tag color={getActionColor(action)}>
                            {action.toUpperCase()}
                        </Tag>
                    ),
                },
                {
                    title: Text.storeHistoryComponent.columns.user,
                    dataIndex: 'cmsUserEmail',
                    key: 'cmsUserEmail',
                    ellipsis: true,
                },
            ],
            []
        );

        const renderContent = () => {
            if (store.isFetchingHistory) {
                return (
                    <div className={style.loading_container}>
                        <Spin size="large" />
                    </div>
                );
            }

            if (selectedRecord) {
                const isNewStore = selectedRecord.beforeValues === null;
                const isDeleted = selectedRecord.afterValues === null;

                return (
                    <div className={style.detail_view}>
                        <Button
                            variant="secondary"
                            icon={<ArrowLeftOutlined />}
                            onClick={handleBackToList}
                            className={style.back_button}
                        >
                            {Text.storeHistoryComponent.backToList}
                        </Button>
                        <div className={style.diff_container}>
                            <div>
                                <div className={style.diff_label}>
                                    {Text.storeHistoryComponent.before}
                                </div>
                                <div className={style.diff_content}>
                                    <StoreChangesDiff
                                        beforeValues={
                                            selectedRecord.beforeValues as StoreValues | null
                                        }
                                        afterValues={
                                            selectedRecord.afterValues as StoreValues | null
                                        }
                                        side="before"
                                        isNewStore={isNewStore}
                                        isDeleted={isDeleted}
                                    />
                                </div>
                            </div>
                            <div>
                                <div className={style.diff_label}>
                                    {Text.storeHistoryComponent.after}
                                </div>
                                <div className={style.diff_content}>
                                    <StoreChangesDiff
                                        beforeValues={
                                            selectedRecord.beforeValues as StoreValues | null
                                        }
                                        afterValues={
                                            selectedRecord.afterValues as StoreValues | null
                                        }
                                        side="after"
                                        isNewStore={isNewStore}
                                        isDeleted={isDeleted}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                );
            }

            return (
                <>
                    <Table
                        columns={columns}
                        dataSource={store.storeHistory?.results ?? []}
                        rowKey="id"
                        pagination={false}
                        className={`${style.custom_table} clickable_table_body_rows`}
                        onRow={(record) => ({
                            onClick: () => handleSelectRecord(record),
                        })}
                    />
                    <div className={style.pagination_container}>
                        <Pagination
                            page={store.storeHistory?.page}
                            pageSize={store.storeHistory?.perPage}
                            total={store.storeHistory?.total}
                            onPageChange={(newPage) => setPage(newPage)}
                            onPageSizeChange={(newPerPage) => {
                                setPerPage(newPerPage);
                                setPage(0);
                            }}
                        />
                    </div>
                </>
            );
        };

        return (
            <Modal
                open={open}
                title={
                    <div className={style.modal_title}>
                        {selectedRecord
                            ? `${Text.storeHistoryComponent.changesTitle} - ${
                                  selectedRecord.storeNref ?? 'N/A'
                              }`
                            : Text.storeHistoryComponent.title}
                    </div>
                }
                footer={null}
                onCancel={handleClose}
                className={style.custom_modal}
                width={1000}
            >
                <div className={style.modal_content}>{renderContent()}</div>
            </Modal>
        );
    }
);
